import React, {
  memo,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
  type UIEvent,
  FC
} from 'react';
import { Button, ButtonVariant, MRTDataTable, ButtonSize } from '@cmhc-schl/hearth-ui';
import { Box } from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useAuthenticationContext } from 'Foundation/Authentication';
import i18n from 'i18next';
import { MRT_SortingState } from 'material-react-table';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styles from './styles.module.scss';
import { HbtMasterPoolSearchResultsTableProps, LoanSearchPoolResponse } from '../../types';
import { createPortfolioColumns } from '../../vriTableColumns';
import { buildSearchFilterApiQuery } from 'Feature/V2/Shared_Portfolio/utils/buildSearchFilterApiQuery';
import { getSearchParams } from 'Feature/V2/Shared_Portfolio/components/SearchFlyout/utils/getSearchParams';
import { getLabel } from 'Feature/V2/Shared_Portfolio/components/SearchFlyout/utils/getLabel';
import { getMasterPoolSearchResultTableData } from '../../api/SearchResultsPageService';
import { orderedSearchQuery } from 'Feature/V2/Shared_Portfolio/components/SearchFlyout/utils/orderedSearchQuery';
import { useMRTCustomTranslation } from 'Feature/V2/Shared_Portfolio/Hooks/useMRTCustomTransaltion';

const fetchSize = 100;
const HbtMasterPoolSearchResultsTable: FC<HbtMasterPoolSearchResultsTableProps> = ({
  toggleSearchDrawer
}) => {
  const authenticationContext = useAuthenticationContext();
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const history = useHistory();
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation('HbtMasterPoolSearchResultPage');
  const { dataType, searchQuery } = getSearchParams(location.search);

  const fetchInventorySearchResultData = async ({ pageParam = 0 }) => {
    const offset = pageParam;

    let sortField = sorting[0]?.id ?? '';
    if (sortField === 'insuredLenderName') {
      sortField = i18n.language === 'en' ? 'insuredLenderEnglishName' : 'insuredLenderFrenchName';
    } else if (sortField === 'underwritingLenderName') {
      sortField =
        i18n.language === 'en' ? 'underwritingLenderEnglishName' : 'underwritingLenderFrenchName';
    }
    const sortOrder = !!sorting[0]?.desc;

    const sizeQueryParams = `?skip=${offset}&take=${fetchSize}`;

    const { searchQuery } = getSearchParams(location.search);

    const searchQueryParams = searchQuery ? buildSearchFilterApiQuery(searchQuery) : '';

    const sortFieldQueryParams = `${
      sortField ? `&orderBy=${sortField} ${sortOrder ? 'desc' : 'asc'}` : ''
    }`;

    const queryParams = `${sizeQueryParams}${searchQueryParams}${sortFieldQueryParams}`;

    const data = await getMasterPoolSearchResultTableData({
      authenticationContext,
      queryParams
    });

    return data;
  };

  const { data, fetchNextPage, isLoading, isFetchingNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: ['masterpool-search-result-table', sorting, location.search],
    queryFn: fetchInventorySearchResultData,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.flat().length;
      const hasMore = lastPage.length === fetchSize;
      return hasMore ? totalFetched : undefined;
    }
  });

  const flatData = useMemo(() => data?.pages.flat() ?? [], [data]);
  const mrtCustomTranslation = useMRTCustomTranslation(flatData.length);

  const columns = useMemo(() => createPortfolioColumns(t, i18n.language), [t, i18n.language]);

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        if (scrollHeight - scrollTop - clientHeight < 400 && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      }
    },
    [fetchNextPage, isFetchingNextPage, hasNextPage]
  );

  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
  }, [fetchMoreOnBottomReached]);

  const onRowSelect = (row: any) => {
    const rowData = row.original;
    const loanId = `&loanId=${rowData.loanId}` || '';
    if (loanId) {
      sessionStorage.setItem('searchLoanDetail', '1');
    }
    const route = 'pages/master-pool/master-pool-details';
    history.push(
      `/${i18n.language}/${route}${location.search}${loanId}&masterPoolCmhcAccountNumber=${
        rowData?.masterPoolCmhcAccountNumber
      }&masterPoolAccountStatusCode=${rowData?.masterPoolStatus}&lenderCode=${rowData?.insuredLenderCode}`
    );
  };

  const handleSearchAgain = () => {
    toggleSearchDrawer && toggleSearchDrawer(true, true);
  };

  const ModifySearch = () => {
    return (
      <>
        {t('Search-Result-No-Data-content2')}
        <Button
          name={t('Search-Result-No-Data-content3')}
          size={ButtonSize.SMALL}
          variant={ButtonVariant.TERTIARY}
          onClick={handleSearchAgain}
          text={{ value: t('Search-Result-No-Data-content3') }}
          ariaText={{ value: t('Search-Result-No-Data-content3') }}
        />
        {t('Search-Result-No-Data-content4')}
      </>
    );
  };

  if (!Object.values(searchQuery)[0]) {
    return (
      <Box>
        <ModifySearch />
      </Box>
    );
  }

  return (
    <Box>
      {!!Object.values(searchQuery)[0] && !isLoading && dataType && (
        <Box className={styles.searchResultText}>
          {flatData.length === 0
            ? t('Search-Result-No-Data-subheader1')
            : flatData.length > 0
              ? t('Search-Result-No-Data-content1')
              : ''}
          {
            <>
              {t(`${getLabel(dataType)}`)}{' '}
              <data className={styles.queryContainer}>
                {Object.values(orderedSearchQuery(searchQuery) ?? {}).map((query, index, array) => (
                  <span key={query}>
                    {query}
                    {index < array.length - 1 && ' '}
                    {index === array.length - 1 && '.'}
                  </span>
                ))}
              </data>{' '}
              <ModifySearch />
              {t('or')}
              <Button
                name={t('clear')}
                size={ButtonSize.SMALL}
                variant={ButtonVariant.TERTIARY}
                onClick={() => {
                  history.push(`/${i18n.language}/pages/master-pool`);
                }}
                text={t('clear').toLocaleLowerCase()}
                ariaText={t('clear')}
              />
              {t('search-results')}
            </>
          }
        </Box>
      )}
      <div className={styles.dataTableContainer}>
        <MRTDataTable<LoanSearchPoolResponse>
          key={'loanSearchPoolResponse'}
          data={flatData}
          columns={columns}
          onRowClick={onRowSelect}
          enableRowVirtualization={true}
          enableColumnVirtualization={true}
          localization={mrtCustomTranslation}
          pageSize={100}
          memoMode="cells"
          columnResizeMode="onChange"
          enableSorting={true}
          muiTableBodyRowProps={({ row }) => ({
            tabIndex: 0,
            onClick: () => onRowSelect(row),
            onKeyDown: (e) => {
              if (e.key === 'Enter') {
                onRowSelect(row);
              }
            }
          })}
          enablePagination={false}
          enableBottomToolbar={false}
          enableStickyHeader={true}
          enableSortingRemoval={false}
          muiTableContainerProps={{
            ref: tableContainerRef,
            sx: { maxHeight: '600px', minHeight: '300px' },
            onScroll: (event: UIEvent<HTMLDivElement>) =>
              fetchMoreOnBottomReached(event.target as HTMLDivElement)
          }}
          manualSorting
          onSortingChange={setSorting}
          state={{
            isLoading,
            sorting
          }}
          renderEmptyRowsFallback={() => <></>}
        />
      </div>
    </Box>
  );
};

export default memo(HbtMasterPoolSearchResultsTable);
